import Phaser from 'phaser'
import { t } from '../../i18n'
import { apiAccountWallet, apiCreateCheckout, apiEconomyConfig, type GemPack, getHandle } from '../../services/JKGame_economy'
import { mountShopOverlay, type ShopOverlayHandle } from '../../ui/JKGame_shopOverlay'
import { audio } from '../JKGame_audio'
import { COLORS } from '../JKGame_ui'

// Gem shop: buy premium-currency packs via REAL Stripe hosted Checkout. The confirm panel hands
// off to Stripe's secure page (apiCreateCheckout → session URL → redirect); gems are credited
// server-side only after Stripe's webhook confirms payment (never client-trusted). On return to
// the game with ?paid=1 the wallet shows a "credited" toast (see init). Cosmetic-economy fair:
// gems buy gacha/cosmetics, never power. Degrades to a localized notice when backend unreachable.
export class JKGame_ShopScene extends Phaser.Scene {
  private alive = true // false after SHUTDOWN; guards async callbacks
  private busy = false // a purchase is in flight — blocks double-buy
  private overlay?: ShopOverlayHandle
  private handle = ''

  constructor() {
    super('Shop')
  }

  create(): void {
    this.alive = true
    this.busy = false
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.overlay?.destroy()
      this.overlay = undefined
      this.input.keyboard?.off('keydown-ESC', backToMenu)
    })
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(240, 0, 0, 0)

    this.overlay = mountShopOverlay({
      title: t('shop.title'),
      loadingText: t('menu.loading'),
      backLabel: t('menu.back'),
      buyLabel: t('shop.buy'),
      cancelLabel: t('gacha.confirm.no'),
      onBack: () => {
        this.scene.start('Menu')
      },
      onPackTap: (pack, total) => {
        audio.play('uiTap')
        this.askBuy(pack, total)
      },
    })
    const backToMenu = (): void => {
      this.scene.start('Menu')
    }
    this.input.keyboard?.on('keydown-ESC', backToMenu)

    void this.init()
  }

  private async init(): Promise<void> {
    try {
      this.handle = getHandle()
      const [cfg, wallet] = await Promise.all([apiEconomyConfig(), apiAccountWallet(this.handle)])
      if (!this.alive) return
      this.overlay?.setBalance(t('shop.balance', { gem: wallet.gem }))
      this.renderPacks(cfg.shop.gem_packs)
      // Returned from a successful Stripe checkout (success_url carries ?paid=1) → confirm the
      // server-side credit (the webhook already applied it; the wallet above reflects it).
      try {
        if (new URLSearchParams(location.search).get('paid') === '1') {
          audio.play('success')
          this.flashStatus(t('shop.success', { gem: wallet.gem }), 'good')
        }
      } catch { /* location unavailable */ }
    } catch {
      if (!this.alive) return
      this.overlay?.setStatus(t('shop.offline'), 'bad') // backend unreachable — never a silent blank
    }
  }

  private renderPacks(packs: GemPack[]): void {
    if (packs.length === 0) {
      this.overlay?.setStatus(t('shop.offline'), 'bad')
      return
    }
    this.overlay?.setPacks(packs.map((pack) => {
      const total = pack.gem + pack.bonus
      return {
        pack,
        total,
        price: `$${(pack.usd_cents / 100).toFixed(2)}`,
        bonusLine: pack.bonus > 0 ? t('shop.bonus', { bonus: pack.bonus }) : '',
      }
    }))
  }

  private askBuy(pack: GemPack, total: number): void {
    if (this.busy) return
    this.overlay?.showConfirm(t('shop.confirm', { gem: total }), () => void this.buy(pack.id))
  }

  private async buy(packId: string): Promise<void> {
    if (this.busy) return
    this.busy = true
    this.overlay?.setBusy(true)
    this.overlay?.setStatus(t('shop.redirecting'), 'sub')
    try {
      const res = await apiCreateCheckout(this.handle, packId)
      if (!this.alive) return
      if (res.ok && res.url) {
        window.location.href = res.url // hand off to Stripe's hosted, secure payment page
        return // navigating away; gems are credited server-side after Stripe confirms (webhook)
      }
      audio.play('error')
      this.overlay?.setStatus(t('shop.offline'), 'bad') // unexpected rejection — surface, never silent
      this.busy = false
      this.overlay?.setBusy(false)
    } catch {
      if (!this.alive) return
      audio.play('error')
      this.overlay?.setStatus(t('shop.offline'), 'bad')
      this.busy = false
      this.overlay?.setBusy(false)
    }
  }

  /** Briefly show a status line (success / error), then fade — cleaned by scene teardown. */
  private flashStatus(text: string, tone: 'good' | 'bad' | 'sub'): void {
    this.overlay?.setStatus(text, tone)
    this.time.delayedCall(2200, () => {
      if (this.alive) this.overlay?.setStatus('')
    })
  }
}
